import { queryOptions } from "@tanstack/react-query";

import { apiUrl } from "@workspace/base-api/apiUrl";
import {
	appFetch,
	ensureOk,
	fetchJson,
	isHttpStatus,
} from "@workspace/base-api/fetchJson";

import type { DeadLetterFilters } from "./dead-letter";
import { parseDeadLetters } from "./dead-letter-response";
import type { HealthData } from "./health-status";

let lastHealthzCallTime: Date | null = null;
let lastDeadLettersCallTime: Date | null = null;

export const devtoolsApi = {
	async getHealthz() {
		const url = new URL("/healthz", apiUrl);
		lastHealthzCallTime = new Date();

		const data = await fetchJson<HealthData>(url.toString(), {
			method: "GET",
		});

		return {
			...data,
			lastCalledAt: lastHealthzCallTime.toISOString(),
		};
	},
	getHealthzOptions: () => {
		return queryOptions({
			queryKey: ["devtools", "healthz"],
			queryFn: () => devtoolsApi.getHealthz(),
			refetchInterval: 30_000, // refetch every 10 seconds
		});
	},

	async getDeadLetters(pageParams?: Record<string, unknown>) {
		const url = new URL("/dead-letters", apiUrl);
		lastDeadLettersCallTime = new Date();

		// HA-879 — the server pages by `limit` and `pageNumber` (it has no cursor).
		const limit = Number(pageParams?.limit ?? 10);
		const page = Number(pageParams?.pageNumber ?? 1);

		const data = await fetchJson<unknown>(url.toString(), {
			method: "POST",
			body: JSON.stringify({ ...pageParams, limit, pageNumber: page }),
		});

		return {
			...parseDeadLetters(data, page, limit),
			lastCalledAt: lastDeadLettersCallTime.toISOString(),
		};
	},
	getDeadLettersOptions: () => {
		return {
			queryKey: ["devtools", "dead-letters"],
			queryFn: devtoolsApi.getDeadLetters,
			refetchInterval: 60_000,
		};
	},
	getPagedDeadLettersOptions: (filters: DeadLetterFilters) => {
		return {
			queryKey: ["devtools", "dead-letters", filters],
			// HA-879 — a caller who is not a platform admin is told so once: no retries, no polling.
			retry: (count: number, error: unknown) =>
				!isHttpStatus(error, 403) && count < 2,
			refetchInterval: (query: { state: { error: unknown } }) =>
				isHttpStatus(query.state.error, 403) ? false : 60_000,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			queryFn: ({ pageParam }: any) => {
				return devtoolsApi.getDeadLetters({ ...pageParam, ...filters });
			},
		};
	},

	async replayDeadLetters(messageIds: string[]) {
		const url = new URL("/dead-letters/replay", apiUrl);

		return await ensureOk(
			await appFetch(url.toString(), {
				method: "POST",
				body: JSON.stringify({ ids: messageIds }),
			}),
		);
	},

	async deleteDeadLetters(messageIds: string[]) {
		const url = new URL("/dead-letters", apiUrl);

		return await ensureOk(
			await appFetch(url.toString(), {
				method: "DELETE",
				body: JSON.stringify({ ids: messageIds }),
			}),
		);
	},
};
